import type { MusicalToken } from '../../types/music';
import type { RhythmicCell } from '../rhythmicCellEngine';
import { isRestMusicalToken, restTokenBeats } from '../scoreImport/restTokens';
import { isRestToken, pitchToVexKey, tokenToVexDuration } from './vexFigureMap';
import { tokenBeats } from '../tokens';

export interface VexNoteSpec {
  duration: string;
  keys: string[];
  isRest: boolean;
  dotted?: boolean;
  /** Índices de notas (en el compás renderizado) que comparten beam. */
  beamWithNext?: boolean;
  /** Casillero incógnita integrado en el pentagrama (índice de tiempo / beatCell). */
  isBlank?: boolean;
  /** Ligadura entrante desde el compás anterior. */
  tieInFromPrevious?: boolean;
  /** Ligadura saliente hacia el compás siguiente. */
  tieOutToNext?: boolean;
}

/** Evento parseado de MusicXML — entrada fiel al render. */
export interface ScoreRenderEvent {
  token: MusicalToken;
  pitch?: string;
  isRest?: boolean;
  dotted?: boolean;
  tieStart?: boolean;
  tieStop?: boolean;
}

export interface MappedVexPattern {
  specs: VexNoteSpec[];
  /** Grupos de índices contiguos para Beam.generateBeams. */
  beamGroups: number[][];
}

const NOTE_KEY = 'f/4';
const REST_KEY = 'b/4';

/** Duración Vex para una nota suelta (no silencio). */
function noteDurationFromToken(token: MusicalToken): string {
  switch (token) {
    case 'E':
      return '8';
    case 'S':
      return '16';
    default:
      return tokenToVexDuration(token);
  }
}

function specFromToken(token: MusicalToken, dotted = false, pitchKey?: string): VexNoteSpec {
  const rest = isRestToken(token);
  return {
    duration: rest ? tokenToVexDuration(token) : noteDurationFromToken(token),
    keys: [rest ? REST_KEY : pitchKey || NOTE_KEY],
    isRest: rest,
    dotted,
  };
}

/** Mapeo fiel token → Vex usando pitches y ligaduras del parser. */
export function mapEventsToVexSpecs(events: ScoreRenderEvent[]): MappedVexPattern & {
  tieInFromPrevious: boolean;
  tieOutToNext: boolean;
} {
  const specs: VexNoteSpec[] = [];
  const beamGroups: number[][] = [];
  let tieInFromPrevious = false;
  let tieOutToNext = false;

  for (let i = 0; i < events.length; i += 1) {
    const ev = events[i]!;
    const dotted = ev.dotted === true;
    const pitchKey = ev.isRest ? REST_KEY : pitchToVexKey(ev.pitch || 'f/4');

    if (i === 0 && ev.tieStop && !ev.tieStart) {
      tieInFromPrevious = true;
    }
    if (i === events.length - 1 && ev.tieStart && !ev.tieStop) {
      tieOutToNext = true;
    }

    const startIdx = specs.length;
    if (ev.token === 'E' && !dotted) {
      specs.push({ duration: '8', keys: [pitchKey], isRest: false });
    } else if (ev.token === 'S' && !dotted) {
      specs.push({ duration: '16', keys: [pitchKey], isRest: false });
    } else {
      const spec = specFromToken(ev.token, dotted, pitchKey);
      if (i === 0 && tieInFromPrevious) spec.tieInFromPrevious = true;
      if (i === events.length - 1 && tieOutToNext) spec.tieOutToNext = true;
      specs.push(spec);
    }

    if (
      specs.length >= 2 &&
      specs[specs.length - 2]!.duration === '8' &&
      specs[specs.length - 1]!.duration === '8' &&
      !specs[specs.length - 2]!.isRest &&
      !specs[specs.length - 1]!.isRest
    ) {
      const prevGroup = beamGroups.find((g) => g[g.length - 1] === specs.length - 2);
      if (prevGroup) prevGroup.push(specs.length - 1);
      else beamGroups.push([specs.length - 2, specs.length - 1]);
    }

    if (specs.length - startIdx >= 4) {
      const slice = specs.slice(-4);
      if (slice.every((s) => s.duration === '16' && !s.isRest)) {
        beamGroups.push([specs.length - 4, specs.length - 3, specs.length - 2, specs.length - 1]);
      }
    }
  }

  return { specs, beamGroups, tieInFromPrevious, tieOutToNext };
}

/** Expande IDs de células rítmicas a especificaciones VexFlow con beams. */
export function mapRhythmicCellToVexSpecs(cell: RhythmicCell, dotted = false): VexNoteSpec[] {
  switch (cell.id) {
    case 'PAR_CORCHEAS':
    case 'DOBLE_CORCHEA':
      return [
        { duration: '8', keys: [NOTE_KEY], isRest: false, beamWithNext: true },
        { duration: '8', keys: [NOTE_KEY], isRest: false },
      ];
    case 'GALOPA':
      return [
        { duration: '8', keys: [NOTE_KEY], isRest: false, beamWithNext: true },
        { duration: '16', keys: [NOTE_KEY], isRest: false, beamWithNext: true },
        { duration: '16', keys: [NOTE_KEY], isRest: false },
      ];
    case 'GALOPA_INV':
      return [
        { duration: '16', keys: [NOTE_KEY], isRest: false, beamWithNext: true },
        { duration: '16', keys: [NOTE_KEY], isRest: false, beamWithNext: true },
        { duration: '8', keys: [NOTE_KEY], isRest: false },
      ];
    case 'CUARTINA':
    case 'PAR_SEMICORCHEAS':
      return Array.from({ length: 4 }, (_, i) => ({
        duration: '16',
        keys: [NOTE_KEY],
        isRest: false,
        beamWithNext: i < 3,
      }));
    case 'SALTILLO':
      return [
        { duration: '8', keys: [NOTE_KEY], isRest: false, beamWithNext: true },
        { duration: '16', keys: [NOTE_KEY], isRest: false },
      ];
    case 'SALTILLO_INV':
      return [
        { duration: '16', keys: [NOTE_KEY], isRest: false, beamWithNext: true },
        { duration: '8', keys: [NOTE_KEY], isRest: false },
      ];
    case 'TRESILLO_CORCHEAS':
      return [
        { duration: '8', keys: [NOTE_KEY], isRest: false, beamWithNext: true },
        { duration: '8', keys: [NOTE_KEY], isRest: false, beamWithNext: true },
        { duration: '8', keys: [NOTE_KEY], isRest: false },
      ];
    case 'SILENCIO_REDONDA':
    case 'REST_W':
      return [{ duration: 'wr', keys: [REST_KEY], isRest: true, dotted }];
    case 'SILENCIO_BLANCA':
    case 'REST_H':
      return [{ duration: 'hr', keys: [REST_KEY], isRest: true, dotted }];
    case 'SILENCIO_NEGRA':
    case 'REST_Q':
      return [{ duration: 'qr', keys: [REST_KEY], isRest: true, dotted }];
    case 'SILENCIO_CORCHEA':
    case 'REST_E':
      return [{ duration: '8r', keys: [REST_KEY], isRest: true, dotted }];
    case 'SILENCIO_SEMICORCHEA':
    case 'REST_S':
      return [{ duration: '16r', keys: [REST_KEY], isRest: true, dotted }];
    default:
      if (cell.isRest && cell.tokens.length === 1) {
        return [specFromToken(cell.tokens[0]!, dotted)];
      }
      return cell.tokens.map((token, i) => ({
        ...specFromToken(token, i === 0 ? dotted : false),
        beamWithNext: i < cell.tokens.length - 1 && !isRestToken(token),
      }));
  }
}

export function mapPatternToVexSpecs(
  pattern: MusicalToken[],
  dottedIndices: number[] = [],
): MappedVexPattern {
  const specs: VexNoteSpec[] = [];
  const beamGroups: number[][] = [];

  for (let i = 0; i < pattern.length; i += 1) {
    const token = pattern[i]!;
    const dotted = dottedIndices.includes(i);
    const startIdx = specs.length;

    if (token === 'E' && !dotted) {
      // Corchea suelta = una corchea con duración 8
      specs.push({ duration: '8', keys: [NOTE_KEY], isRest: false });
    } else if (token === 'S' && !dotted) {
      specs.push({ duration: '16', keys: [NOTE_KEY], isRest: false });
    } else {
      specs.push(specFromToken(token, dotted));
    }

    // Dos corcheas consecutivas → beam
    if (
      specs.length >= 2 &&
      specs[specs.length - 2]!.duration === '8' &&
      specs[specs.length - 1]!.duration === '8' &&
      !specs[specs.length - 2]!.isRest &&
      !specs[specs.length - 1]!.isRest
    ) {
      const prevGroup = beamGroups.find((g) => g[g.length - 1] === specs.length - 2);
      if (prevGroup) {
        prevGroup.push(specs.length - 1);
      } else {
        beamGroups.push([specs.length - 2, specs.length - 1]);
      }
    }

    // Cuatro semicorcheas consecutivas
    if (specs.length - startIdx >= 4) {
      const slice = specs.slice(-4);
      if (slice.every((s) => s.duration === '16' && !s.isRest)) {
        beamGroups.push([specs.length - 4, specs.length - 3, specs.length - 2, specs.length - 1]);
      }
    }
  }

  return { specs, beamGroups };
}

export function beatsForVexSpec(spec: VexNoteSpec): number {
  if (spec.isRest) {
    if (spec.duration === 'wr') return 4;
    if (spec.duration === 'hr') return 2;
    if (spec.duration === 'qr') return 1;
    if (spec.duration === '8r') return 0.5;
    if (spec.duration === '16r') return 0.25;
    return 1;
  }
  if (spec.duration === 'w') return 4;
  if (spec.duration === 'h') return 2;
  if (spec.duration === 'q') return 1;
  if (spec.duration === '8') return 0.5;
  if (spec.duration === '16') return 0.25;
  return 1;
}

export function truncateSpecsToBarCapacity(
  specs: VexNoteSpec[],
  maxBeats: number,
): VexNoteSpec[] {
  let sum = 0;
  const out: VexNoteSpec[] = [];
  for (const spec of specs) {
    const beats = beatsForVexSpec(spec) * (spec.dotted ? 1.5 : 1);
    if (sum + beats > maxBeats + 0.01) break;
    out.push(spec);
    sum += beats;
  }
  return out.length ? out : specs.slice(0, 1);
}

export function truncateTokensToBarCapacity(
  pattern: MusicalToken[],
  timeSignature: string,
): MusicalToken[] {
  const [numStr, denStr] = String(timeSignature || '4/4').split('/');
  const numerator = Number(numStr) || 4;
  const denominator = Number(denStr) || 4;
  const maxBeats = numerator * (4 / denominator);

  let sum = 0;
  const out: MusicalToken[] = [];
  for (const token of pattern) {
    const beats = isRestMusicalToken(token) ? restTokenBeats(token) : tokenBeats(token);
    if (sum + beats > maxBeats + 0.01) break;
    out.push(token);
    sum += beats;
  }
  return out.length ? out : pattern.slice(0, 1);
}
